// Angular Files
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Other External Files
import { firstValueFrom } from 'rxjs';

// Payment Integration Files
import {
 IFramePaymentResponse, IFrameSavePaymentMethodResponse, PaymentMethodData, PaymentMethodTypeEnum, PaymentMethodTypeEnumConvertor
} from 'apps/public-portal/src/app/payment-integrations/base/models';
// For some reason if the PaymentProcessorService was included from just base, the unit tests would fail
import { PaymentProcessorService } from 'apps/public-portal/src/app/payment-integrations/base/interfaces';

// Teller Online Files
import {
    AuthorizationConsentDto,
    FisIntegrationApiClient,
    FisPaymentDetailsRequestDto,
    FisPaymentDetailsResponseDto,
    FisVerifyPaymentRequestDto,
    IFramePaymentRequestDto,
    IFrameSavePaymentMethodRequestDto,
    IFrameSavePaymentMethodResponseDto,
    PaymentStatusEnumDto,
    SessionTokenResponseDto,
    SharedPaymentIntegrationApiClient
} from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';
import {
    IFramePaymentPrepareResponse
} from 'apps/public-portal/src/app/payment-integrations/base/models/IFramePaymentPrepareResponse';

@Injectable({
    providedIn: 'root'
})
export class FisService extends PaymentProcessorService {
    // #region PaymentProcessorService fields

    // The endpoint used to load Component.js
    public tokenizationUrl: string = null;

    public multiPayMerchantAlias: string = null;

    public multiPayIFrameUrl: string = null;

    public useMultiPay: boolean | undefined = null;

    // #endregion

    constructor(
        private fisApi: FisIntegrationApiClient,
        private router: Router,
        sharedPaymentApi: SharedPaymentIntegrationApiClient
    ) {
        super(sharedPaymentApi);
    }

    // #region PaymentProcessorService

    public override async checkout() {
        this.router.navigate(['/checkout']);
    }

    public override get hasPrepareStep() { return true; }

    public async preparePayment(params: any): Promise<any> {
        const response = await this.preparePaymentAndAssessFees(params);
        return new FisPaymentPrepareResponse(response);
    }

    public override async payCart(params): Promise<IFramePaymentResponse> {
        const request = this._buildDto(params);

        // Make the sale (Validate cart, complete payment transaction, post to teller).
        const response = await firstValueFrom(
            this.sharedPaymentApi.makePayment(request)
        );

        return new IFramePaymentResponse({
            emailAddress: response.emailAddress,
            errorMessage: response.responseMessage,
            cartStatus: response.responseSuccess
        });
    }

    public override async savePaymentMethod(params): Promise<IFrameSavePaymentMethodResponse> {
        const request = new IFrameSavePaymentMethodRequestDto();
        const { paymentMethodData }: { paymentMethodData: PaymentMethodData } = params;

        request.captchaToken = params.captchaToken;

        this.mapRequest(request, paymentMethodData, params.paymentToken);
        if (params.authorizationConsent) {
            request.authorizationConsent = params.authorizationConsent;
        }

        let response: IFrameSavePaymentMethodResponseDto;
        if (params.paymentMethodId) {
            response = await firstValueFrom(this.sharedPaymentApi.updatePaymentMethod(params.paymentMethodId, request));
        } else {
            response = await firstValueFrom(this.sharedPaymentApi.addPaymentMethod(request));
        }

        return new IFrameSavePaymentMethodResponse({
            paymentMethodId: response.paymentMethodId,
            last4: request.last4
        });
    }

    /** Used for MultiPay scenarios.
     * Note, because this is used for multipay, the payment should start in Processing, not PreProcessing.
     * Unlike the tokenization payments which normally start in PreProcessing.
     * As a note, Processing is the default for this method in general, but because it's different from how FIS functions normally I
     * specified it explicitly
     */
    public async startPayment(cartGuid: string): Promise<string> {
        const response = await firstValueFrom(this.sharedPaymentApi.startPayment(cartGuid, PaymentStatusEnumDto.Processing));
        return response.paymentIdentifier;
    }
    // #endregion

    // #region fis methods

    public async requestJwt(paymentMethodType: PaymentMethodTypeEnum, throwError: boolean = false, forMultiPay: boolean = false): Promise<SessionTokenResponseDto> {
        try {
            return await firstValueFrom(this.fisApi.requestJwt(PaymentMethodTypeEnumConvertor.toDto(paymentMethodType), forMultiPay));
        } catch (e) {
            if (throwError) {
                throw e;
            } else {
                this._errorEvent.next('Failed to generate session token');
                return undefined;
            }
        }
    }

    /** Used for MultiPay scenarios */
    public async getPaymentDetails(cartGuid: string, captchaToken: string): Promise<FisPaymentDetailsResponseDto> {
        const body = new FisPaymentDetailsRequestDto({ cartGuid, captchaToken });
        return firstValueFrom(this.fisApi.fisGeneratePaymentDetails(body));
    }

    /** Used for MultiPay scenarios */
    public async verifyPayment(cartGuid: string, paymentMethodData: PaymentMethodData, paymentId: string, paymentIdentifier: string, authorizationConsent: AuthorizationConsentDto = null): Promise<IFramePaymentResponse> {
        const request = new FisVerifyPaymentRequestDto({ paymentId, cartGuid, paymentIdentifier });
        if (paymentMethodData != null) {
            request.last4 = paymentMethodData.last4Digits;
            request.cardType = paymentMethodData.cardType;
            request.email = paymentMethodData.billingInfo.email;
            request.processorPaymentMethodType = paymentMethodData.processorPaymentMethodType;
        }
        // TOP-2935: the MultiPay iFrame charges client-side, so this verify call
        // is what carries the user's authorization consent to the backend.
        if (authorizationConsent) {
            request.authorizationConsent = authorizationConsent;
        }
        const response = await firstValueFrom(this.fisApi.fisVerifyPayment(request));

        return new IFramePaymentResponse({
            emailAddress: response.emailAddress,
            errorMessage: response.responseMessage,
            cartStatus: response.responseSuccess
        });
    }
    // #endregion

    // #region helpers

    public async preparePaymentAndAssessFees(params): Promise<IFramePaymentPrepareResponse> {
        const request = this._buildDto(params);

        // Just prepare the payment for submission to receive the calculated fees,
        // but do not submit the payment until the user approves the fees.
        const response = await firstValueFrom(this.fisApi.preparePaymentAndAssessFees(request));

        return new IFramePaymentPrepareResponse({
            paymentToken: response.paymentToken,
            convenienceFee: response.convenienceFee,
            processorPaymentMethodType: response.processorPaymentMethodType
        });
    }

    public _buildDto(params): IFramePaymentRequestDto {
        const request = new IFramePaymentRequestDto();

        const { paymentMethodData }: { paymentMethodData: PaymentMethodData } = params;
        request.cartId = params.cartId;
        request.cartGuid = params.cartGuid;
        request.paymentMethodType = PaymentMethodTypeEnumConvertor.toDto(params.paymentMethodType ?? paymentMethodData.type);
        request.inboundRedirectSourceId = params.inboundRedirectSourceId;
        request.validationToken = params.validationToken;
        request.processorPaymentMethodType = params.processorPaymentMethodType;
        request.captchaToken = params.captchaToken;

        // TOP-2935: forward the user's consent payload to the backend. May be
        // null for legitimate Credit no-op flows where no version resolves;
        // server-side `AuthorizationConsentManager` enforces the actual
        // required/optional decision per PaymentMethodType.
        if (params.authorizationConsent) {
            request.authorizationConsent = params.authorizationConsent;
        }
        if (params.savedPaymentMethodAuthorizationConsent) {
request.savedPaymentMethodAuthorizationConsent = params.savedPaymentMethodAuthorizationConsent;
        }

        if (!params.saved) {
            this.mapRequest(request, paymentMethodData, params.paymentToken);
        }

        return request;
    }

    // #endregion
}

/** Helper class to ease converting IFramePaymentPrepareResponse to an object
 * with keys that match that of the params that payCart is expecting.
 */
class FisPaymentPrepareResponse {
    public validationToken: string;

    public convenienceFee: number;

    public processorPaymentMethodType: string;

    constructor(response: IFramePaymentPrepareResponse) {
        this.validationToken = response.paymentToken;
        this.convenienceFee = response.convenienceFee;
        this.processorPaymentMethodType = response.processorPaymentMethodType;
    }
}
